import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Building2, ChevronRight } from "lucide-react";

import type { NavArea } from "analytics";
import { useUserProfileMenu } from "components/common/UserProfileDropdown";
import { PATH_WORKSPACE } from "config";
import {
  PERSONAL_SETTINGS_NAV_GROUPS,
  PERSONAL_SETTINGS_NOTIFICATIONS_SECTION_ID,
  PERSONAL_SETTINGS_TITLE,
  PERSONAL_SETTINGS_WORKSPACE_GROUP_LABEL,
  SETTINGS_HUB_SECTION_ID,
  WORKSPACE_SETTINGS_NAV_GROUPS,
  WORKSPACE_SETTINGS_TITLE,
  isSettingsSectionActive,
  type SettingsNavGroup,
} from "config/navigation";
import { useWorkspaceApi, useWorkspaceIdentifiers } from "hooks/useWorkspace";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import { useWorkspaceSettings } from "hooks/useWorkspace/useWorkspaceSettings";
import { SETTINGS_SECTIONS, type SettingsSection } from "pages/settings/Settings.sections";
import {
  WORKSPACE_SETTINGS_SECTIONS,
  type WorkspaceSettingsSection,
} from "pages/settings/workspace/WorkspaceSettings.sections";
import { filterVisibleWorkspaceSettingsSections } from "pages/settings/workspace/workspaceSettingsVisibility";
import { Permissions } from "utils/permissions/permissionConstants";
import { resolveWorkspacePath } from "utils/routing/workspaceRouteUtils";
import { AppSidebar } from "./AppSidebar";
import { useAppSidebarLayout } from "./appSidebarLayout";
import { AppSidebarFooter, NotificationBadge, NotificationDot } from "./AppSidebarFooter";
import { AppSidebarHeader } from "./AppSidebarHeader";
import { useLastProjectLocation } from "./lastProjectLocation";
import { CollapsedTooltip, SidebarRowLink, SidebarSectionLabel } from "./SidebarRow";
import { useTrackNavClick } from "./useTrackNavClick";

type SectionWithIcon = Pick<SettingsSection | WorkspaceSettingsSection, "id" | "title" | "icon">;

interface SettingsNavLink {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  active: boolean;
  /** Accessible name when it says more than the label, e.g. the unread count. */
  ariaLabel?: string;
  trailing?: React.ReactNode;
  collapsedAdornment?: React.ReactNode;
}

type SettingsNavGroups = Array<{ id: string; label: string; links: SettingsNavLink[] }>;

const renderSectionIcon = (icon: React.ReactNode) =>
  React.isValidElement<{ className?: string }>(icon)
    ? React.cloneElement(icon, { "className": "h-4 w-4 shrink-0", "aria-hidden": true } as never)
    : icon;

/** Builds the grouped links of a settings area from its section list and grouping. */
export function buildSettingsNavGroups<TSection extends SectionWithIcon>(
  groups: readonly SettingsNavGroup[],
  sections: readonly TSection[],
  resolveHref: (section: TSection) => string,
  pathname: string
): SettingsNavGroups {
  const byId = new Map(sections.map((section) => [section.id, section]));
  return groups
    .map((group) => ({
      id: group.id,
      label: group.label,
      links: group.sectionIds
        .map((id) => byId.get(id))
        .filter((section): section is TSection => Boolean(section))
        .map((section) => {
          const href = resolveHref(section);
          return {
            id: section.id,
            label: section.title,
            icon: renderSectionIcon(section.icon),
            href,
            active: isSettingsSectionActive(pathname, href, section.id === SETTINGS_HUB_SECTION_ID),
          };
        }),
    }))
    .filter((group) => group.links.length > 0);
}

const SettingsNav = ({ area, groups }: { area: NavArea; groups: SettingsNavGroups }) => {
  const trackNavClick = useTrackNavClick();
  return (
    <>
      {groups.map((group) => (
        <div key={group.id}>
          <SidebarSectionLabel>{group.label}</SidebarSectionLabel>
          <div className="flex flex-col gap-px">
            {group.links.map((link) => (
              <CollapsedTooltip key={link.id} label={link.ariaLabel ?? link.label}>
                <SidebarRowLink
                  to={link.href}
                  icon={link.icon}
                  label={link.label}
                  aria-label={link.ariaLabel}
                  active={link.active}
                  trailing={link.trailing}
                  collapsedAdornment={link.collapsedAdornment}
                  onClick={() => trackNavClick({ area, item: link.id })}
                />
              </CollapsedTooltip>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

/** "Back to <project>" when the user came from a project, else back to all projects. */
const BackRow = ({ workspaceId }: { workspaceId: string }) => {
  const lastProject = useLastProjectLocation();
  const target =
    lastProject && (!workspaceId || lastProject.workspaceId === workspaceId)
      ? { href: lastProject.path, label: `Back to ${lastProject.projectName}` }
      : workspaceId
        ? {
            href: resolveWorkspacePath(PATH_WORKSPACE.workspaceProjects, workspaceId),
            label: "Back to all projects",
          }
        : null;
  if (!target) return null;
  return (
    <CollapsedTooltip label={target.label}>
      <SidebarRowLink
        to={target.href}
        icon={<ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />}
        label={target.label}
        className="min-h-7 text-chrome-muted-foreground"
      />
    </CollapsedTooltip>
  );
};

const AreaTitle = ({ children }: { children: string }) => {
  const { collapsed } = useAppSidebarLayout();
  if (collapsed) return null;
  return <div className="px-2 pt-1 text-sm font-semibold text-chrome-foreground">{children}</div>;
};

/** Sidebar of `/workspaces/:id/settings/*`, gated by workspace permissions. */
export const WorkspaceSettingsSidebar = () => {
  const { pathname } = useLocation();
  const { workspaceId: urlWorkspaceId = "" } = useParams<{ workspaceId: string }>();
  const { workspaceId } = useWorkspaceIdentifiers();
  const { canWorkspace } = useWorkspaceSettings(workspaceId || "");

  const groups = React.useMemo(() => {
    const visibleIds = new Set(
      filterVisibleWorkspaceSettingsSections(canWorkspace).map((section) => section.id)
    );
    return buildSettingsNavGroups(
      WORKSPACE_SETTINGS_NAV_GROUPS,
      WORKSPACE_SETTINGS_SECTIONS.filter((section) => visibleIds.has(section.id)),
      (section) => resolveWorkspacePath(section.path, urlWorkspaceId),
      pathname
    );
  }, [canWorkspace, pathname, urlWorkspaceId]);

  return (
    <AppSidebar
      label="Workspace settings navigation"
      header={
        <AppSidebarHeader>
          <BackRow workspaceId={urlWorkspaceId} />
          <AreaTitle>{WORKSPACE_SETTINGS_TITLE}</AreaTitle>
        </AppSidebarHeader>
      }
      footer={<AppSidebarFooter />}
    >
      <SettingsNav area="workspace_settings" groups={groups} />
    </AppSidebar>
  );
};

/**
 * Sidebar of the personal settings pages (`/settings/*`): Account, Security
 * and Billing pages, then a link to the current workspace's settings for
 * people who may read them. Notifications carries the unread badge here, so
 * the footer leaves its own Notifications row out.
 */
export const PersonalSettingsSidebar = () => {
  const { pathname } = useLocation();
  const { workspaceId } = useWorkspaceIdentifiers();
  const { workspaces } = useWorkspaceApi({ take: 50 });
  const resolvedWorkspaceId = workspaceId || workspaces[0]?.id || "";
  // Same check as the projects home's Workspace settings row.
  const { canWorkspace, isLoading: workspacePermissionsLoading } =
    useWorkspaceAuth(resolvedWorkspaceId);
  const canOpenWorkspaceSettings =
    Boolean(resolvedWorkspaceId) &&
    !workspacePermissionsLoading &&
    canWorkspace(Permissions.WORKSPACE_READ);
  const { hasUnread, unreadCount, displayCount } = useUserProfileMenu();

  const groups = React.useMemo(() => {
    const sectionGroups = buildSettingsNavGroups(
      PERSONAL_SETTINGS_NAV_GROUPS,
      SETTINGS_SECTIONS,
      (section) => section.path,
      pathname
    ).map((group) => ({
      ...group,
      links: group.links.map((link) =>
        link.id === PERSONAL_SETTINGS_NOTIFICATIONS_SECTION_ID && hasUnread
          ? {
              ...link,
              ariaLabel: `${link.label}, ${unreadCount} unread`,
              trailing: <NotificationBadge count={displayCount} />,
              collapsedAdornment: <NotificationDot />,
            }
          : link
      ),
    }));

    if (!canOpenWorkspaceSettings) return sectionGroups;
    return [
      ...sectionGroups,
      {
        id: "workspace",
        label: PERSONAL_SETTINGS_WORKSPACE_GROUP_LABEL,
        links: [
          {
            id: "workspace-settings",
            label: WORKSPACE_SETTINGS_TITLE,
            icon: <Building2 className="h-4 w-4 shrink-0" aria-hidden />,
            href: resolveWorkspacePath(PATH_WORKSPACE.workspaceSettings, resolvedWorkspaceId),
            active: false,
            trailing: (
              <ChevronRight
                className="h-3.5 w-3.5 shrink-0 text-chrome-muted-foreground"
                aria-hidden
              />
            ),
          },
        ],
      },
    ];
  }, [
    canOpenWorkspaceSettings,
    displayCount,
    hasUnread,
    pathname,
    resolvedWorkspaceId,
    unreadCount,
  ]);

  return (
    <AppSidebar
      label="Settings navigation"
      header={
        <AppSidebarHeader>
          <BackRow workspaceId={resolvedWorkspaceId} />
          <AreaTitle>{PERSONAL_SETTINGS_TITLE}</AreaTitle>
        </AppSidebarHeader>
      }
      footer={<AppSidebarFooter showNotifications={false} />}
    >
      <SettingsNav area="personal_settings" groups={groups} />
    </AppSidebar>
  );
};
